import { cpVerdict } from '@felix/domain';
import { Button, Progress, Skeleton, Table, UnstyledButton } from '@mantine/core';
import {
  IconAnchor,
  IconBell,
  IconBolt,
  IconCalendarTime,
  IconCheck,
  IconChevronDown,
  IconCircleCheckFilled,
  IconClock,
  IconFileText,
  IconFlag,
  IconFlag3Filled,
  IconGauge,
  IconLink,
  IconMapPin,
  IconNote,
  IconPencil,
  IconPlus,
  IconRoute,
  IconShip,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { TOKENS } from '../../tokens.js';
import { errorCode } from '../personnel/source.js';
import { ScrollTable } from './fields.js';
import shared from './NoonReportScreen.module.css';
import { formatStamp, SectionTitle } from './NoonSections.js';
import { nextDue } from './next-due.js';
import { formatPlannedTime, plannedFact } from './noon-report.js';
import { useNumber } from './numbers.js';
import {
  cpCompliancePct,
  cpStatusRows,
  emptyLogAction,
  formatDuration,
  legEventPath,
  logRows,
  plannedHours,
  speedGaugePct,
  type TreeGroup,
  track,
  voyageDelayHours,
  voyageTree,
} from './overview.js';
import { linkThenTimeline } from './screen-load.js';
import type { VoyageSource } from './source.js';
import type { Voyage, VoyageReport } from './types.js';
import { NextDueText, VoyageStartNotice } from './VoyageContextNotices.js';
import { type FrameData, VOYAGE_TONE, VoyageFrame } from './VoyageFrame.js';
import classes from './VoyageOverviewScreen.module.css';
import { pendingVoyageStart } from './voyage-start.js';

export { VOYAGE_TONE };

const REPORT_TONE: Record<VoyageReport['status'], StatusTone> = {
  draft: 'warning',
  submitted: 'success',
  reopened: 'warning',
  void: 'danger',
};

/** Seyir günlüğünde gösterilen son satır sayısı; "View all" hepsini açar (G4-45). */
const LOG_ROWS = 8;

/** Seferin rapor zamanını gün ve saat olarak (gemi saatiyle değil, UTC; ekrandaki "Time (UTC)"). */
const day = (iso: string) => formatStamp(iso).slice(0, 10);
const time = (iso: string) => formatStamp(iso).slice(11);

/** Planlanan CP hızı: yüklüde laden, yoksa ballast. */
const plannedSpeedOf = (voyage: Voyage) => voyage.cpSpeedLadenKn ?? voyage.cpSpeedBallastKn ?? null;

export interface VoyageOverviewScreenProps {
  source: VoyageSource;
}

/**
 * Sefer özeti (müşteri ekranı `gemi-sefer-yonetimi.jpeg`; ADR 0022, ADR 0019; M6f G4-38…G4-50):
 * seferin başlık kartı, sefer ağacı, özet değerler, iz, hız ve CP performansı, seyir günlüğü,
 * temel değerler, uyarılar ve hızlı işlemler. Gemide ve ofiste aynı ekran; sefer ofiste planlanır.
 */
export function VoyageOverviewScreen({ source }: VoyageOverviewScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const n = useNumber();
  const k = (key: string) => t(`voyage.overview.${key}`);
  return (
    <VoyageFrame
      source={source}
      title={k('title')}
      description={k('description')}
      crumb={k('title')}
      facts={(voyage) => {
        const planned = plannedSpeedOf(voyage);
        return [
          { label: k('totalDistance'), value: n.unit(voyage.plannedDistanceNm, 'NM', 0) },
          { label: k('plannedSpeed'), value: n.unit(planned, 'kn', { digits: 1, fixed: true }) },
          {
            label: k('plannedTime'),
            value: formatDuration(plannedHours(voyage.plannedDistanceNm, planned)),
          },
          // Başlık kartında yalnız ETA (yerel saat, altında fark); ETD "Key Figures"ta (ADR 0054 §2)
          plannedFact(k('etaUtc'), voyage.plannedEta, voyage.plannedEtaTzOffsetMin),
        ];
      }}
      action={({ voyage }) =>
        source.planner ? (
          <Button
            leftSection={<IconPencil size={16} />}
            onClick={() =>
              shell?.navigate(`/voyage/new?vessel=${voyage.vesselId}&voyage=${voyage.id}`)
            }
          >
            {k('editVoyage')}
          </Button>
        ) : null
      }
      skeleton={
        <div className={classes.grid}>
          <Skeleton height={640} radius="md" />
          <div className={classes.center}>
            <Skeleton height={120} radius="md" />
            <Skeleton height={300} radius="md" />
            <Skeleton height={320} radius="md" />
          </div>
          <div className={classes.side}>
            <Skeleton height={300} radius="md" />
            <Skeleton height={200} radius="md" />
          </div>
        </div>
      }
    >
      {(data) => <OverviewBody data={data} source={source} />}
    </VoyageFrame>
  );
}

function OverviewBody({ data, source }: { data: FrameData; source: VoyageSource }) {
  const { t } = useTranslation();
  const shell = useShell();
  const n = useNumber();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.overview.${key}`, options ?? {});
  const { voyage, reports, performance } = data;
  const overall = performance?.performance.overall ?? null;
  const cp = performance?.cp ?? null;
  const plannedSpeed = plannedSpeedOf(voyage);
  const avgSpeed = performance?.performance.cp?.avgSpeedKn ?? overall?.avgSpeedKn ?? null;
  const planned = plannedHours(voyage.plannedDistanceNm, plannedSpeed);
  const arrival = reports.filter((r) => r.kind === 'arrival' && r.status === 'submitted').at(-1);
  const delay = voyageDelayHours(voyage.plannedEta, reports);
  const go = (path: string) => shell?.navigate(path);
  const one = { digits: 1, fixed: true } as const;
  const onTarget = avgSpeed !== null && plannedSpeed !== null && avgSpeed >= plannedSpeed;
  const status = cpStatusRows({
    plannedSpeedKn: plannedSpeed,
    avgSpeedKn: avgSpeed,
    plannedDistanceNm: voyage.plannedDistanceNm,
    distanceNm: overall?.distanceNm ?? null,
    plannedHours: planned,
    hours: overall?.hours ?? null,
  });
  const pct = cpCompliancePct(avgSpeed, plannedSpeed);
  // Tüketim bilinmiyorsa "CP içinde" denmez, kısmi gösterilir (KARAR #96)
  const verdict = cpVerdict(cp);
  // Seferi başlatılmamış kalkış ve kuyruğu (ADR 0157 §2–§3): gemide bu sefer hedefse önce bağla;
  // ofiste salt okunur uyarı ve Sefer Talebi. Bağdan sonra çizgi yeniden okunur (#2278)
  const [timeline, setTimeline] = useState<VoyageReport[] | null>(null);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  useEffect(() => {
    if (!source.allReports) return;
    let live = true;
    source
      .allReports(voyage.vesselId)
      .then((list) => live && setTimeline(list))
      .catch(() => live && setTimeline(null));
    return () => {
      live = false;
    };
  }, [source, voyage.vesselId]);
  const pending = timeline ? pendingVoyageStart(timeline, data.voyages) : null;
  const linkHere = pending?.targets.some((v) => v.id === voyage.id) ? pending : null;
  const linkFirst = () => {
    if (!linkHere || !source.planner?.link) return;
    setLinking(true);
    setLinkError(null);
    linkThenTimeline(
      source,
      voyage.id,
      voyage.vesselId,
      linkHere.queue.map((r) => r.id),
    )
      .then((fresh) => {
        setTimeline(fresh);
        data.reload?.();
      })
      .catch((error: unknown) => setLinkError(errorCode(error)))
      .finally(() => setLinking(false));
  };
  // Raporsuz seferde gemi ilk raporu açar, ofis bekler (ortağın notu #1; ADR 0047 §1)
  const emptyAction = emptyLogAction(
    voyage.status,
    reports,
    !!source.editor,
    linkHere && source.planner?.link ? linkHere.queue.length : 0,
  );
  const createDeparture = () => go(legEventPath(voyage.id, 'departure'));
  const due = source.editor ? nextDue(reports, new Date()) : null;
  const firstActions = {
    onCreateDeparture: createDeparture,
    onLinkFirst: linkFirst,
    linkCount: linkHere?.queue.length ?? 0,
    linking,
  };

  return (
    <div className={classes.grid}>
      <VoyageTreeCard
        voyage={voyage}
        reports={reports}
        emptyAction={emptyAction}
        actions={firstActions}
      />

      <div className={classes.center}>
        {pending ? (
          <VoyageStartNotice
            pending={pending}
            onStart={
              source.planner
                ? () =>
                    go(
                      `/voyage/new?departure=${pending.departure.id}&back=${encodeURIComponent(
                        `/voyage/list?voyage=${voyage.id}`,
                      )}`,
                    )
                : null
            }
            onLink={linkHere && source.planner?.link ? () => linkFirst() : null}
            busy={linking}
            onOpen={() =>
              go(
                source.editor
                  ? `/voyage/legs?voyage=none&report=${pending.departure.id}`
                  : `/voyage/legs?vessel=${voyage.vesselId}&voyage=none&report=${pending.departure.id}`,
              )
            }
            // Ofis: salt okunur not ve mevcut sefer talebi (ADR 0083 §5; All Reports ile aynı)
            onRequest={
              !source.planner && source.requestChange
                ? () =>
                    go(`/voyage/new?vessel=${voyage.vesselId}&departure=${pending.departure.id}`)
                : null
            }
          />
        ) : null}
        {linkError ? (
          <p className={shared.message} data-tone="danger" role="alert">
            {t(`voyage.errors.${linkError}`, { defaultValue: t('voyage.errors.GENERIC') })}
          </p>
        ) : null}
        <section className={shared.card} aria-label={k('summary')}>
          <SectionTitle title={k('summary')} />
          <div className={classes.tiles}>
            <SummaryTile
              icon={<IconRoute size={24} />}
              label={k('totalDistance')}
              value={n(voyage.plannedDistanceNm, 0)}
              unit="NM"
            />
            <SummaryTile
              icon={<IconMapPin size={24} />}
              label={k('actualDistance')}
              value={n(overall?.distanceNm ?? null, 0)}
              unit="NM"
            />
            <SummaryTile
              icon={<IconGauge size={24} />}
              label={k('plannedSpeed')}
              value={n(plannedSpeed, one)}
              unit="kn"
            />
            <SummaryTile
              icon={<IconShip size={24} />}
              label={k('avgSpeed')}
              value={n(avgSpeed, one)}
              unit="kn"
              tone={onTarget ? 'success' : undefined}
            />
            <SummaryTile
              icon={<IconClock size={24} />}
              label={k('totalTime')}
              value={formatDuration(overall?.hours)}
            />
            <SummaryTile
              icon={<IconCalendarTime size={24} />}
              label={k('delay')}
              value={delay === null ? '—' : formatDuration(Math.abs(delay))}
              {...(delay !== null && delay < 0 ? { prefix: '−' } : {})}
              muted={delay === null}
              {...(delay === null ? { title: k('delayUnknown') } : {})}
            />
          </div>
        </section>

        <div className={classes.pair}>
          <section className={[shared.card, classes.trackCard].join(' ')} aria-label={k('track')}>
            {track(reports).length > 1 ? (
              <TrackMap
                points={track(reports)}
                from={voyage.fromPort}
                to={voyage.toPort}
                arrived={!!arrival}
              />
            ) : (
              <EmptyState icon={<IconMapPin size={22} stroke={1.8} />} title={k('noTrack')} />
            )}
          </section>
          <div className={classes.stack}>
            <section className={shared.card} aria-label={k('speedPerformance')}>
              <SectionTitle title={k('speedPerformance')} divider={false} />
              <div className={classes.speed}>
                <SpeedGauge
                  pct={speedGaugePct(avgSpeed, plannedSpeed)}
                  value={n.unit(avgSpeed, 'kn', one)}
                  label={k('avgSpeed')}
                  good={onTarget}
                />
                <dl className={classes.speedFacts}>
                  <div>
                    <dt>{k('planned')}</dt>
                    <dd>{n.unit(plannedSpeed, 'kn', one)}</dd>
                  </div>
                  <div>
                    <dt>{k('difference')}</dt>
                    <dd
                      data-tone={
                        cp?.speedDiffKn == null
                          ? undefined
                          : cp.speedDiffKn >= 0
                            ? 'success'
                            : 'warning'
                      }
                    >
                      {cp?.speedDiffKn === null || cp?.speedDiffKn === undefined
                        ? '—'
                        : `${cp.speedDiffKn > 0 ? '+' : ''}${n.unit(cp.speedDiffKn, 'kn', one)}`}
                    </dd>
                    {verdict.speed === null ? null : (
                      <dd
                        className={classes.cpLine}
                        data-tone={verdict.speed ? 'success' : 'warning'}
                      >
                        <IconCircleCheckFilled size={14} aria-hidden />
                        {verdict.speed ? k('onCharter') : k('offCharter')}
                      </dd>
                    )}
                  </div>
                </dl>
              </div>
            </section>
            <section className={shared.card} aria-label={k('cpCompliance')}>
              <SectionTitle title={k('cpCompliance')} divider={false} />
              <div className={classes.barRow}>
                <Progress
                  value={pct ?? 0}
                  color={pct !== null && pct >= 100 ? TOKENS['success-fg'] : TOKENS['warning-500']}
                  size="md"
                  radius="xl"
                  aria-label={k('cpCompliance')}
                  className={classes.bar}
                />
                <strong>{pct === null ? '—' : `${pct}%`}</strong>
                {pct !== null && pct >= 100 ? (
                  <span className={classes.withinCp}>
                    {k('withinCp')}
                    <IconCheck size={14} aria-hidden />
                  </span>
                ) : null}
              </div>
              <dl className={classes.splitFacts}>
                <div>
                  <dt>{k('allowedSpeed')}</dt>
                  <dd>{n.unit(plannedSpeed, 'kn', one)}</dd>
                </div>
                <div>
                  <dt>{k('avgSpeed')}</dt>
                  <dd>{n.unit(avgSpeed, 'kn', one)}</dd>
                </div>
              </dl>
            </section>
          </div>
        </div>

        <VoyageLog
          reports={reports}
          canAdd={!!source.editor}
          onAdd={() => go(legEventPath(voyage.id))}
          emptyAction={emptyAction}
          actions={firstActions}
        />

        <div className={classes.pair} data-even>
          <section className={shared.card} aria-label={k('notes')}>
            <SectionTitle icon={<IconNote size={20} />} title={k('notes')} />
            {voyage.remarks || voyage.notes ? (
              <div className={classes.notes}>
                {[voyage.remarks, voyage.notes].filter(Boolean).map((note) => (
                  <p key={String(note)}>{note}</p>
                ))}
              </div>
            ) : (
              <EmptyState icon={<IconFileText size={22} stroke={1.8} />} title={k('noNotes')} />
            )}
          </section>
          <section className={shared.card} aria-label={k('documents')}>
            <SectionTitle icon={<IconFileText size={20} />} title={k('documents')} />
            <EmptyState
              icon={<IconFileText size={22} stroke={1.8} />}
              title={k('noDocuments')}
              description={k('documentsHint')}
            />
          </section>
        </div>
      </div>

      <div className={classes.side}>
        <section className={shared.card} aria-label={k('keyFigures')}>
          <SectionTitle title={k('keyFigures')} />
          <dl className={classes.keyFigures}>
            {(
              [
                [k('departurePort'), voyage.fromPort],
                [k('arrivalPort'), voyage.toPort],
                [k('distanceShort'), n.unit(voyage.plannedDistanceNm, 'NM', 0)],
                [k('plannedSpeed'), n.unit(plannedSpeed, 'kn', one)],
                [k('avgSpeed'), n.unit(avgSpeed, 'kn', one), onTarget ? 'success' : undefined],
                [k('totalTime'), formatDuration(overall?.hours)],
                [k('etd'), formatPlannedTime(voyage.plannedEtd, voyage.plannedEtdTzOffsetMin)],
                [k('etaUtc'), formatPlannedTime(voyage.plannedEta, voyage.plannedEtaTzOffsetMin)],
                [k('actualArrival'), arrival ? formatStamp(arrival.reportedAt) : '—'],
              ] as [string, string, string | undefined][]
            ).map(([label, value, tone]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd data-tone={tone}>{value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className={`${shared.card} ${classes.cpCard}`} aria-label={k('cpStatus')}>
          <SectionTitle
            title={k('cpStatus')}
            aside={
              verdict.status === 'unknown' ? null : (
                <StatusBadge
                  tone={
                    verdict.status === 'within'
                      ? 'success'
                      : verdict.status === 'partial'
                        ? 'info'
                        : 'warning'
                  }
                  withIcon
                >
                  {k(`verdict.${verdict.status}`)}
                </StatusBadge>
              )
            }
          />
          <ScrollTable label={k('cpStatusTable')}>
            <Table className={[shared.table, classes.cpTable].join(' ')} withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{k('parameter')}</Table.Th>
                  <Table.Th>{k('planned')}</Table.Th>
                  <Table.Th>{k('actual')}</Table.Th>
                  <Table.Th>{k('status')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {status.map((row) => {
                  const cells: Record<typeof row.key, [string, string, string]> = {
                    speed: [
                      k('avgSpeed'),
                      n.unit(plannedSpeed, 'kn', one),
                      n.unit(avgSpeed, 'kn', one),
                    ],
                    distance: [
                      k('totalDistance'),
                      n.unit(voyage.plannedDistanceNm, 'NM', 0),
                      n.unit(overall?.distanceNm ?? null, 'NM', 0),
                    ],
                    time: [
                      k('totalTime'),
                      planned === null ? '—' : `≤ ${formatDuration(planned)}`,
                      formatDuration(overall?.hours),
                    ],
                  };
                  const [label, plan, actual] = cells[row.key];
                  return (
                    <Table.Tr key={row.key}>
                      <Table.Td>{label}</Table.Td>
                      <Table.Td>{plan}</Table.Td>
                      <Table.Td>{actual}</Table.Td>
                      <Table.Td>
                        {row.ok === null ? (
                          '—'
                        ) : (
                          <StatusBadge tone={row.ok ? 'success' : 'warning'} withIcon>
                            <span className={classes.cpStatusText}>
                              {row.ok ? k('ok') : k('check')}
                            </span>
                          </StatusBadge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollTable>
        </section>

        <OverviewAlerts
          reports={reports}
          within={cp?.withinLimits ?? null}
          onAll={() => go(legEventPath(voyage.id))}
        />

        {source.editor ? (
          <section className={shared.card} aria-label={k('quickActions')}>
            <SectionTitle icon={<IconBolt size={20} />} title={k('quickActions')} />
            {due ? (
              // Sıradaki rapor ve zamanı (ADR 0157 §5): plan raporun Next Operation'ı; form açılmaz
              <p className={classes.nextDue} data-testid="next-due">
                <NextDueText due={due} />
              </p>
            ) : null}
            <div className={classes.quick}>
              <Button
                leftSection={<IconFileText size={18} />}
                onClick={() => go(`/voyage/noon-report?voyage=${voyage.id}`)}
              >
                {k('createNoon')}
              </Button>
              <Button
                leftSection={<IconAnchor size={18} />}
                onClick={() => go(legEventPath(voyage.id, 'arrival'))}
              >
                {k('createArrival')}
              </Button>
              <Button
                leftSection={<IconFlag size={18} />}
                onClick={() => go(legEventPath(voyage.id, 'cosp'))}
              >
                {k('createCosp')}
              </Button>
              <Button
                leftSection={<IconFlag3Filled size={18} />}
                onClick={() => go(legEventPath(voyage.id, 'eosp'))}
              >
                {k('createEosp')}
              </Button>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

/** Özet kutucuğu: ikon solda iki satır boyunca, etiket ve değer; birim küçük (G4-42, G4-67). */
export function SummaryTile({
  icon,
  label,
  value,
  unit,
  tone,
  muted = false,
  prefix,
  title,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  unit?: string;
  tone?: 'success' | undefined;
  muted?: boolean;
  prefix?: string;
  title?: string;
}) {
  return (
    <div className={classes.tile} data-tone={tone} data-muted={muted || undefined} title={title}>
      <span className={classes.tileIcon} aria-hidden>
        {icon}
      </span>
      <span className={classes.tileLabel}>{label}</span>
      <strong className={classes.tileValue}>
        {prefix}
        {value}
        {unit && value !== '—' ? <small>{` ${unit}`}</small> : null}
      </strong>
    </div>
  );
}

/** ≈ 270° hız göstergesi: değer ortalama hız / (CP hızı × 1,2); hep dolu değil (G4-43). */
function SpeedGauge({
  pct,
  value,
  label,
  good,
}: {
  pct: number | null;
  value: string;
  label: string;
  good: boolean;
}) {
  const size = 132;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const arc = circumference * 0.75;
  const filled = arc * ((pct ?? 0) / 100);
  return (
    <div className={classes.gauge} role="img" aria-label={`${label}: ${value}`}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden>
        <g transform={`rotate(135 ${size / 2} ${size / 2})`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            className={classes.gaugeTrack}
            strokeWidth={stroke}
            strokeDasharray={`${arc} ${circumference}`}
            strokeLinecap="round"
          />
          {pct === null ? null : (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              className={classes.gaugeValue}
              data-tone={good ? 'success' : 'warning'}
              strokeWidth={stroke}
              strokeDasharray={`${filled} ${circumference}`}
              strokeLinecap="round"
            />
          )}
        </g>
      </svg>
      <span className={classes.gaugeLabel}>
        <strong>{value}</strong>
        <span>{label}</span>
      </span>
    </div>
  );
}

/**
 * İz: rapor konumları; başlangıç (yeşil iğne, kalkış limanı), son konum (gemi) ya da varış
 * (kırmızı iğne). Çevrimdışı gemide harita karosu yok: hafif enlem-boylam ağı (kabul edilen fark;
 * G4-41).
 */
function TrackMap({
  points,
  from,
  to,
  arrived,
}: {
  points: { lat: number; lon: number }[];
  from: string;
  to: string;
  arrived: boolean;
}) {
  const { t } = useTranslation();
  const width = 480;
  const height = 300;
  const pad = 36;
  const lats = points.map((p) => p.lat);
  const lons = points.map((p) => p.lon);
  const [minLat, maxLat] = [Math.min(...lats), Math.max(...lats)];
  const [minLon, maxLon] = [Math.min(...lons), Math.max(...lons)];
  const span = Math.max(
    (maxLon - minLon) / (width - 2 * pad),
    (maxLat - minLat) / (height - 2 * pad),
    0.0001,
  );
  const cx = (minLon + maxLon) / 2;
  const cy = (minLat + maxLat) / 2;
  const x = (lon: number) => width / 2 + (lon - cx) / span;
  const y = (lat: number) => height / 2 - (lat - cy) / span;
  const xy = points.map((p) => [x(p.lon), y(p.lat)] as const);
  const first = xy[0];
  const last = xy.at(-1);
  // Etiketin yönü için uçtan en az 12 px uzaktaki ilk nokta (COSP limana çok yakın olabilir)
  const away = (point: readonly [number, number], list: readonly (readonly [number, number])[]) =>
    list.find(([px, py]) => Math.hypot(px - point[0], py - point[1]) > 12);
  // Etiket izin karşı tarafında: komşu nokta yukarıdaysa aşağıda; sağ kenarda sola yaslı
  const labelAt = (
    point: readonly [number, number],
    neighbour: readonly [number, number] | undefined,
  ) => {
    const below = neighbour ? neighbour[1] < point[1] : point[1] < 40;
    const fits = below ? point[1] + 26 < height : point[1] - 22 > 0;
    const under = fits ? below : !below;
    const end = point[0] > width - 120;
    return {
      x: end ? -10 : 10,
      y: under ? 22 : -12,
      textAnchor: end ? ('end' as const) : ('start' as const),
    };
  };
  // Ağ çizgileri 10°'lik adımla
  const step = 10;
  const meridians: number[] = [];
  for (let lon = Math.ceil((cx - (width / 2) * span) / step) * step; x(lon) < width; lon += step) {
    meridians.push(lon);
  }
  const parallels: number[] = [];
  for (let lat = Math.ceil((cy - (height / 2) * span) / step) * step; y(lat) > 0; lat += step) {
    parallels.push(lat);
  }
  const label = t('voyage.overview.trackLabel', { from, to, count: points.length });
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={classes.track}
      role="img"
      aria-label={label}
      preserveAspectRatio="xMidYMid meet"
    >
      <rect x="0" y="0" width={width} height={height} className={classes.trackSea} />
      {meridians.map((lon) => (
        <line
          key={`m${lon}`}
          x1={x(lon)}
          x2={x(lon)}
          y1={0}
          y2={height}
          className={classes.trackGrid}
        />
      ))}
      {parallels.map((lat) => (
        <line
          key={`p${lat}`}
          x1={0}
          x2={width}
          y1={y(lat)}
          y2={y(lat)}
          className={classes.trackGrid}
        />
      ))}
      <polyline
        points={xy.map(([px, py]) => `${px},${py}`).join(' ')}
        className={classes.trackLine}
        fill="none"
      />
      {xy.slice(1, -1).map(([px, py], index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: konumların kimliği yok; sıra zamana göre
        <circle key={index} cx={px} cy={py} r={3.5} className={classes.trackDot} />
      ))}
      {first ? (
        <g transform={`translate(${first[0]} ${first[1]})`}>
          <Pin tone="start" />
          <text {...labelAt(first, away(first, xy.slice(1)))} className={classes.trackLabel}>
            {from}
          </text>
        </g>
      ) : null}
      {last ? (
        <g transform={`translate(${last[0]} ${last[1]})`}>
          {arrived ? (
            <>
              <Pin tone="end" />
              <text
                {...labelAt(last, away(last, xy.slice(0, -1).reverse()))}
                className={classes.trackLabel}
              >
                {to}
              </text>
            </>
          ) : (
            <>
              <circle r={11} className={classes.trackShipHalo} />
              <g transform="translate(-8 -8)" className={classes.trackShip}>
                <IconShip size={16} stroke={2} />
              </g>
            </>
          )}
        </g>
      ) : null}
    </svg>
  );
}

function Pin({ tone }: { tone: 'start' | 'end' }) {
  return (
    <g className={classes.pin} data-tone={tone}>
      <path d="M0 0 C-6 -8 -8 -11 -8 -15 A8 8 0 1 1 8 -15 C8 -11 6 -8 0 0 Z" />
      <circle cx={0} cy={-15} r={3.2} className={classes.pinDot} />
    </g>
  );
}

/**
 * Sefer ağacı (G4-38, G4-39): zaman çizgisi; aşamalar katlanır, varsayılan olarak yalnız güncel
 * aşama açık. Noon raporları tek özet satırı. Kart orta sütunun boyundadır, içi kayar.
 */
type EmptyAction = ReturnType<typeof emptyLogAction>;

/** Raporsuz seferin ilk eylemleri (ADR 0047 §1; ADR 0157 §3: önce bağla). */
interface FirstActions {
  onCreateDeparture: () => void;
  onLinkFirst: () => void;
  linkCount: number;
  linking: boolean;
}

/** "Önce bağla (n)" birincil, "Kalkış raporu gir" ikincil (ADR 0157 §3, #2266). */
function FirstReportButtons({
  emptyAction,
  actions,
  size,
}: {
  emptyAction: EmptyAction;
  actions: FirstActions;
  size: 'compact-sm' | 'sm';
}) {
  const { t } = useTranslation();
  if (emptyAction === 'linkFirst') {
    return (
      <span className={shared.noticeActions}>
        <Button
          size={size}
          loading={actions.linking}
          leftSection={<IconLink size={14} />}
          onClick={actions.onLinkFirst}
        >
          {t('voyage.context.linkFirst', { count: actions.linkCount })}
        </Button>
        <Button
          size={size}
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={actions.onCreateDeparture}
        >
          {t('voyage.overview.createDeparture')}
        </Button>
      </span>
    );
  }
  return (
    <Button
      size={size}
      variant={size === 'sm' ? 'filled' : 'light'}
      leftSection={<IconPlus size={14} />}
      onClick={actions.onCreateDeparture}
    >
      {t('voyage.overview.createDeparture')}
    </Button>
  );
}

function VoyageTreeCard({
  voyage,
  reports,
  emptyAction,
  actions,
}: {
  voyage: Voyage;
  reports: readonly VoyageReport[];
  emptyAction: EmptyAction;
  actions: FirstActions;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.overview.${key}`, options ?? {});
  const tree = voyageTree(reports);
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(tree.map((group) => [group.stage, group.current])),
  );
  return (
    <section className={[shared.card, classes.treeCard].join(' ')} aria-label={k('tree')}>
      <SectionTitle
        title={k('tree')}
        aside={<span className={classes.timeNote}>{k('timeUtc')}</span>}
      />
      <div className={classes.treeScroll}>
        <ol className={classes.tree}>
          <li className={classes.node}>
            <span className={classes.nodeDot} data-done aria-hidden>
              <IconPlus size={11} stroke={3} />
            </span>
            <div>
              <strong>{k('created')}</strong>
              <span>{k('voyageNo', { no: voyage.voyageNo })}</span>
            </div>
          </li>
          {tree.map((group) => (
            <TreeStageRow
              key={group.stage}
              group={group}
              emptyAction={group.stage === 'departure' ? emptyAction : null}
              actions={actions}
              open={!!open[group.stage]}
              onToggle={() =>
                setOpen((state) => ({ ...state, [group.stage]: !state[group.stage] }))
              }
            />
          ))}
          <li className={classes.node} data-end>
            <span
              className={classes.nodeDot}
              data-done={voyage.status === 'completed' || undefined}
              aria-hidden
            >
              <IconCheck size={11} stroke={3} />
            </span>
            <strong>{k('completedNode')}</strong>
          </li>
        </ol>
      </div>
    </section>
  );
}

const STAGE_ICON = { departure: IconAnchor, enRoute: IconRoute, arrival: IconFlag3Filled } as const;

function TreeStageRow({
  group,
  open,
  onToggle,
  emptyAction,
  actions,
}: {
  group: TreeGroup;
  open: boolean;
  onToggle: () => void;
  emptyAction: EmptyAction;
  actions: FirstActions;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.overview.${key}`, options ?? {});
  const Icon = STAGE_ICON[group.stage];
  const id = `tree-${group.stage}`;
  return (
    <li className={classes.stage}>
      <UnstyledButton
        className={classes.stageHead}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
      >
        <span className={classes.stageIcon} aria-hidden>
          <Icon size={12} stroke={2.4} />
        </span>
        <span className={classes.stageTitle}>{k(`stages.${group.stage}`)}</span>
        <IconChevronDown
          size={16}
          className={classes.chevron}
          data-open={open || undefined}
          aria-hidden
        />
      </UnstyledButton>
      {open ? (
        group.items.length === 0 ? (
          <div id={id} className={classes.stageEmpty}>
            {emptyAction === 'createDeparture' || emptyAction === 'linkFirst' ? (
              <FirstReportButtons emptyAction={emptyAction} actions={actions} size="compact-sm" />
            ) : (
              <p>{emptyAction === 'awaitShip' ? k('reportsFromShip') : k('noReportsStage')}</p>
            )}
          </div>
        ) : (
          <ol id={id} className={classes.items}>
            {group.items.map((item, index) => (
              <li key={item.key} className={classes.item}>
                <span
                  className={classes.itemMarker}
                  data-tone={item.status === 'submitted' ? 'success' : 'warning'}
                  aria-hidden
                >
                  {group.stage === 'departure' ? `${index + 1}.` : <IconFileText size={14} />}
                </span>
                <span className={classes.itemText}>
                  <strong>{item.count ? k('noonReports') : t(`voyage.kinds.${item.kind}`)}</strong>
                  <span>
                    {item.count && item.first !== item.last
                      ? `${day(item.first)} – ${day(item.last)}`
                      : formatStamp(item.first)}
                  </span>
                </span>
                {item.count ? (
                  <StatusBadge tone={item.status === 'submitted' ? 'success' : 'info'}>
                    {`${item.count.submitted}/${item.count.total}`}
                  </StatusBadge>
                ) : (
                  <StatusBadge tone={REPORT_TONE[item.status]}>
                    {t(`voyage.reportStatus.${item.status}`)}
                  </StatusBadge>
                )}
              </li>
            ))}
          </ol>
        )
      ) : null}
    </li>
  );
}

/** Seyir günlüğü: son satırlar ve "View all"; tarihe göre sıralanır (G4-45). */
function VoyageLog({
  reports,
  canAdd,
  onAdd,
  emptyAction,
  actions,
}: {
  reports: readonly VoyageReport[];
  canAdd: boolean;
  onAdd: () => void;
  emptyAction: EmptyAction;
  actions: FirstActions;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string) => t(`voyage.overview.${key}`);
  const [all, setAll] = useState(false);
  const [ascending, setAscending] = useState(true);
  const rows = logRows(reports);
  const shown = all ? rows : rows.slice(-LOG_ROWS);
  const ordered = ascending ? shown : [...shown].reverse();
  return (
    <section className={`${shared.card} ${classes.logCard}`} aria-label={k('log')}>
      <SectionTitle
        title={k('log')}
        aside={
          canAdd ? (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={onAdd}>
              {k('addReport')}
            </Button>
          ) : null
        }
      />
      {rows.length === 0 ? (
        <EmptyState
          icon={<IconFileText size={22} stroke={1.8} />}
          title={k('noReports')}
          {...(emptyAction
            ? {
                description:
                  emptyAction === 'awaitShip' ? k('reportsFromShipHint') : k('firstReportHint'),
              }
            : {})}
          action={
            emptyAction === 'createDeparture' || emptyAction === 'linkFirst' ? (
              <FirstReportButtons emptyAction={emptyAction} actions={actions} size="sm" />
            ) : null
          }
        />
      ) : (
        <>
          <ScrollTable label={k('logTable')}>
            <Table className={[shared.table, classes.logTable].join(' ')}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th aria-sort={ascending ? 'ascending' : 'descending'}>
                    <UnstyledButton
                      className={classes.sort}
                      onClick={() => setAscending((v) => !v)}
                    >
                      {k('date')}
                      {ascending ? (
                        <IconSortAscending size={14} aria-hidden />
                      ) : (
                        <IconSortDescending size={14} aria-hidden />
                      )}
                    </UnstyledButton>
                  </Table.Th>
                  <Table.Th>{k('reportType')}</Table.Th>
                  <Table.Th data-col="time">{k('timeUtc')}</Table.Th>
                  <Table.Th data-numeric data-col="speed">
                    {k('speed')}
                  </Table.Th>
                  <Table.Th data-numeric data-col="distance">
                    {k('distance')}
                  </Table.Th>
                  <Table.Th>{k('status')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {ordered.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      {day(row.reportedAt)}
                      {/* Saat sütunu dar kartta gizlenince saat tarihin altında (M6f G7) */}
                      <span className={classes.logWhen}>{time(row.reportedAt)}</span>
                    </Table.Td>
                    <Table.Td>{t(`voyage.kinds.${row.kind}`)}</Table.Td>
                    <Table.Td data-col="time">{time(row.reportedAt)}</Table.Td>
                    <Table.Td data-numeric data-col="speed">
                      {n(row.speedKn, { digits: 1, fixed: true })}
                    </Table.Td>
                    <Table.Td data-numeric data-col="distance">
                      {n(row.distanceNm, { digits: 1, fixed: true })}
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge tone={REPORT_TONE[row.status]}>
                        {t(`voyage.reportStatus.${row.status}`)}
                      </StatusBadge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollTable>
          {rows.length > LOG_ROWS ? (
            <Button
              variant="subtle"
              size="xs"
              className={classes.viewAll}
              onClick={() => setAll((v) => !v)}
            >
              {all ? k('showLatest') : t('voyage.overview.viewAllCount', { count: rows.length })}
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}

/** Uyarılar: ikon çipi, metin, sağda saat; CP durumu satırı (G4-47). */
function OverviewAlerts({
  reports,
  within,
  onAll,
}: {
  reports: readonly VoyageReport[];
  within: boolean | null;
  onAll: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.overview.${key}`, options ?? {});
  const drafts = reports.filter((r) => r.status !== 'submitted');
  return (
    <section className={shared.card} aria-label={k('alerts')}>
      <SectionTitle
        icon={<IconBell size={20} />}
        title={k('alerts')}
        aside={
          <Button variant="subtle" size="compact-xs" onClick={onAll}>
            {k('viewAll')}
          </Button>
        }
      />
      <ul className={classes.alerts}>
        {drafts.map((r) => (
          <li key={r.id}>
            <span className={classes.alertDot} data-tone="warning" aria-hidden>
              <IconClock size={12} stroke={2.4} />
            </span>
            <span>{k('draftPending', { kind: t(`voyage.kinds.${r.kind}`) })}</span>
            <time>{formatStamp(r.reportedAt)}</time>
          </li>
        ))}
        {within === null ? null : (
          <li>
            <span
              className={classes.alertDot}
              data-tone={within ? 'success' : 'warning'}
              aria-hidden
            >
              <IconCheck size={12} stroke={3} />
            </span>
            <span>{within ? k('speedWithin') : k('speedBelow')}</span>
            <time>{k('throughout')}</time>
          </li>
        )}
        {drafts.length === 0 && within === null ? (
          <li>
            <span className={classes.alertDot} data-tone="success" aria-hidden>
              <IconCheck size={12} stroke={3} />
            </span>
            <span>{k('noAlerts')}</span>
            <time />
          </li>
        ) : null}
      </ul>
    </section>
  );
}
